import * as R from 'ramda';
import {
  takeLatest,
  select,
  put,
  spawn,
} from 'redux-saga/effects';
import {
  ti2 as ti2Apis,
  aiRules as aiRulesApis,
  generativeAi as genaiApis,
} from '../../../api.js';
import { apiSaga } from '../../../root-sagas.js';
import { _getAccountInfo } from './partner-settings/edit-app-key-configuration-sagas.js';
import { fetchAsyncJob as fetchAsyncJobImport } from '../../../utils/fetchAsyncJob.js';

function* onLocationChange({ payload }) {
  const pathname = R.pathOr('', ['location', 'pathname'], payload);
  if (pathname.includes('generative-ai/integration-settings/rules')) {
    const partners = yield select(R.pathOr([], ['PM', 'partnerList', 'list', 'partners']));
    if (partners.length === 0) {
      yield put({ type: '@partner-list/GET_PARTNERS' })
    }
    const allRules = yield select(R.pathOr([], ['generativeAi', 'aiRules', 'allRules']));
    if (allRules.length === 0) {
      yield apiSaga('genAi_aiRules_loadAllRules', aiRulesApis.getAIRules, [{}]);
    }
    const { companyId, ti2token } = yield _getAccountInfo();

    const allAppKeys = yield select(R.pathOr([], ['generativeAi', 'aiRules', 'allAppKeys']));
    if (allAppKeys.length === 0) {
      yield apiSaga('genAi_aiRules_loadAllAppKeys', ti2Apis.getAllAppKeys, [{ companyId, ti2token }])
    }
  }
}

function* deleteRules({ payload }) {
  for (const ruleId of payload.ruleIds) {
    yield apiSaga('genAi_aiRules_deleteOneRule', aiRulesApis.deleteAIRule, [ruleId, { skipLoader: true }])
  }
}

function* doAssignPartners() {
  const { selectedPartnersToAdd, selectedPartnersToRemove, rules, appliedToAllPartners } = yield select(state => state.generativeAi.aiRules.assignPartners);
  for (const rule of rules) {
    yield apiSaga('genAi_aiRules_assignPartners_forOneRule', aiRulesApis.updateRule, [{
      id: rule.id,
      scope: appliedToAllPartners ? 'any' : null,
      partnerIds: appliedToAllPartners ? [] : R.uniq([
        ...selectedPartnersToAdd.map(p => p.id),
        // HAVE to include the existing partners
        ...rule.Partners
          .filter(p => selectedPartnersToRemove.every(spr => spr.id !== p.id))
          .map(p => p.id),
      ]),
    }, {
      skipLoader: true,
    }])
  }
  const { rules: updatedRules } = yield select(state => state.generativeAi.aiRules.assignPartners);
  if (updatedRules.every(o => o.success)) {
    yield put({ type: 'genAi_aiRules_assignPartners_assign_SUCCESS' })
  }
}

function* _doSaveRule() {
  const { id, matcher, body, content, ruleType } = yield select(state => state.generativeAi.aiRules.editRule);
  yield apiSaga('genAi_aiRules_editRule_save', aiRulesApis.updateRule, [{
    id,
    content: ruleType === 'product' && body.options
      ? `If the service name contains the text: ${matcher}, please add ${
      body.options.length === 1
      ? ''
      : `ALL ${['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX'][body.options.length]} of `
    }the following service line${body.options.length === 1 ? '' : 's'} to the result: ${JSON.stringify(body.options)}` : content,
    matcher,
    body,
    ruleType,
  }]);
}

/*
 Format of payload - when NO Rule matches
  [{
    "jobId": "runModuleProcess:1ee44d1e-b1fb-4aac-9749-feb5fda796af",
    "status": "success",
    "result": {
      "products": null,
      "rules": [],
      "wining_product": null,
      "wining_rule": null
    },
    "info": {
      "process": "complete",
      "result": {
        "products": null,
        "rules": [],
        "wining_product": null,
        "wining_rule": null
      }
    }
  }]

Format of payload - when One ore More Rule match
 There will be multiple entries. 
 Rule should not be saved when there are more than one wining rules. As this is a conflict
*/
function* shouldSaveRuleOrNot({ payload }) {
  // Check if any wining rule is found
  const { id } = yield select(state => state.generativeAi.aiRules.editRule);
  
  // the payload is always an array with the first object being the result of the evaluation for this rule
  const wining_rule_found = R.path([0, 'result', 'wining_rule'], payload);

  if (!(wining_rule_found && wining_rule_found.ruleId === id)) {
    // not able to locate this rule in the wining rule
    return;
  }

  // No conflicting rule found. Save this rule!
  yield _doSaveRule();
}

function* doProductRuleEvalInEditRule() {
  const { matcher, ruleType, rulePartners, id, body } = yield select(state => state.generativeAi.aiRules.editRule);
  if (ruleType !== 'product') {
    yield _doSaveRule();
    return;
  }
  const allJobs = yield _getMatcherEvalJobs({
    evalRuleId: id,
    actionType: 'genAi_aiRules_editRule_productRuleEval',
    Partners: rulePartners,
    evalMatcher: matcher,
    testMatcher: body.extractedServiceName || matcher,
  });
  yield spawn(fetchAsyncJobImport('genAi_aiRules_editRule_productRuleEval', genaiApis.checkVectorJobStatus), { jobs: allJobs });
}

/*
Format of Partners Parameter:
  [
    {"id":"de37b750-5be0-11ef-b154-3fef98f3838d","ownerCompanyId":"5aaac20738a0a0120087e217",
    "ownerUserId":"5aaac20738a0a0120087e216","linkedAccountId":null,"avatar":null,"name":"Audley-Test",
    "description":null,"website":null,"phone":null,"addressOne":null,"addressTwo":null,"city":null,
    "state":null,"zip":null,"country":null,"point":null,"origin":"bookingsai-affiliate","archived":false,
    "archivedAt":null,"createdAt":"2024-08-16T15:04:43.000Z","updatedAt":"2024-08-16T15:04:43.000Z",
    "lastInteractedAt":"2024-08-16T15:04:43.000Z","locked":false,"billingState":0,"misc":null,
    "AIRule_Partner": {"createdAt":"2024-08-14T20:25:52.260Z","updatedAt":"2024-08-14T20:25:52.260Z",
          "ruleId":7,"partnerId":"de37b750-5be0-11ef-b154-3fef98f3838d"}
    },
    {"id":"fb1f7730-9472-11ee-8ff8-7fd56f5106f4","ownerCompanyId":"5aaac20738a0a0120087e217",
    "ownerUserId":"5aaac20738a0a0120087e216","linkedAccountId":null,"avatar":null,
    "name":"Audley","description":null,"website":null,"phone":null,"addressOne":null,
    "addressTwo":null,"city":null,"state":null,"zip":null,"country":null,"point":null,
    "origin":"bookingsai-affiliate","archived":null,"archivedAt":null,"createdAt":"2024-08-14T20:25:52.247Z",
    "updatedAt":"2024-08-14T20:25:52.247Z","lastInteractedAt":"2024-08-14T20:25:52.247Z","locked":false,
    "billingState":0,"misc":null,
    "AIRule_Partner":{"createdAt":"2024-08-28T18:23:56.660Z",
        "updatedAt":"2024-08-28T18:23:56.660Z","ruleId":7,"partnerId":"fb1f7730-9472-11ee-8ff8-7fd56f5106f4"}
    }
  ]
*/
function* _getMatcherEvalJobs({ evalRuleId, actionType, Partners, testMatcher, evalMatcher, additionalPayload }) {
  const allAppKeys = yield select(state => state.generativeAi.aiRules.allAppKeys);
  let allJobs = [];
  for (const p of Partners) {
    const foundIntegration = allAppKeys.find(k => k.hint === p.id);
    if (!foundIntegration) continue;
    const { jobs} = yield apiSaga(actionType, aiRulesApis.productRuleEval, [{
      testMatcher,
      ti2: {
        url: process.env.REACT_APP_TI2_URL,
        app: 'tourplan',
        hint: p.id,
        token: yield select(R.path(['account', 'travelMLSettings', 'ti2', 'token'])),
      },
      // The conflict should be checked with all other rules except the rule been evaluated
      // NOTE: when ruleIdEdited is sent the backend will remove this rule while evaluating
      // the conflict 
      // Send the Rule Id for the current rule been edited for both cases:
      // 1. User clicks "Evaluate" for a single rule
      // 2. User Open/Edit's a rule and then saves it
      // ruleIdEdited: (p.AIRule_Partner.ruleId) ? p.AIRule_Partner.ruleId : null,
      evalRuleId,
      evalMatcher,
      scopes: [p.id],
      companyId: yield select(R.path(['account', 'company', 'companyId'])),
    }, additionalPayload]);
    allJobs = [...allJobs, ...jobs];
  }
  return allJobs;
}

function* evalFirstOneFromList() {
  const { allRules, rulesToEvaluate } = yield select(state => state.generativeAi.aiRules);
  const firstRule = allRules.find(o => o.id === rulesToEvaluate[0]);
  if (!firstRule) return;
  const allJobs = yield _getMatcherEvalJobs({
    actionType: 'genAi_aiRules_productRuleEval_singleRow',
    evalRuleId: firstRule.id,
    evalMatcher: firstRule.matcher,
    Partners: firstRule.Partners,
    testMatcher: R.path(['body', 'extractedServiceName'], firstRule) || firstRule.matcher,
    additionalPayload: { id: firstRule.id, skipLoader: true },
  });
  yield spawn(fetchAsyncJobImport('genAi_aiRules_productRuleEval_singleRow', genaiApis.checkVectorJobStatus), {
    jobs: allJobs,
    additionalPayload: { id: firstRule.id },
  });
}

// eslint-disable-next-line
export default [
  takeLatest('@@router/LOCATION_CHANGE', onLocationChange),
  takeLatest('genAi_aiRules_deleteRules', deleteRules),
  takeLatest('genAi_aiRules_assignPartners_assign', doAssignPartners),
  takeLatest('genAi_aiRules_editRule_onClickSave', doProductRuleEvalInEditRule),
  // takeLatest('genAi_aiRules_evaluation_evaluateText', doTextEvalInOneRule),
  takeLatest([
    'genAi_aiRules_evaluateSelectedProductRules',
    'genAi_aiRules_productRuleEval_singleRow_JobCheckComplete',
  ], evalFirstOneFromList),
  takeLatest('genAi_aiRules_editRule_productRuleEval_JobCheckComplete', shouldSaveRuleOrNot),
];